import { IItem } from '@bimser/core';
import { GetIconComponent } from '@bimser/icons';
import IconClear from '@bimser/icons/16/close-panel';
import { Picker } from 'antd-mobile';
import classNames from 'classnames/bind';
import React from 'react';
import BCButton from '../BCButton';
import BCButtonGroup from '../BCButtonGroup';
import BCInputSearch, { IInputChangeEventArgs } from '../BCInputSearch';
import { findMessage } from '../BCIntl';
import Styles from './assets/mobilePickerStyles.scss';
import { IPickerProps } from './entities';

const cx = classNames.bind(Styles);

export default function BCMobilePicker(props: IPickerProps) {
	const [visible, setVisible] = React.useState(false);
	const [options, setOptions] = React.useState(props.options);
	const [searchValue, setSearchValue] = React.useState('');

	React.useEffect(() => {
		setOptions(
			props.options.map(item => ({
				...item,
				selected: props.value === item.value
			}))
		);
	}, [props.options, props.value]);

	const titleValue = React.useMemo(() => options.find(item => item.selected)?.text, [options]);

	const onConfirm = pickerValue =>
		props.onSelectedChange({
			senderArgs: null,
			data: pickerValue[0]
		});

	const onClose = () => {
		setVisible(false);
		onSearch({ data: '' });
	};

	const clearValue = () => onConfirm([]);

	const onClickPickerButton = () => setVisible(true);

	const getItems = () => {
		let items = options;

		if (searchValue) {
			items = options.filter(item =>
				item.text.toLowerCase().includes(searchValue.toLowerCase())
			);
		}

		return items.map(option => ({
			key: option.key,
			value: option.value,
			label: (
				<div>
					{renderIcon(option)}
					<span>{option.text}</span>
				</div>
			)
		}));
	};

	const onSearch = ({ data }: IInputChangeEventArgs) => {
		if (props.onSearch) {
			props.onSearch(data);
		}

		setSearchValue(data);
	};

	const getSearchInput = () => {
		if (props.showSearch) {
			return (
				<BCInputSearch
					className={Styles.picker}
					onChange={onSearch}
				/>
			);
		}

		return <React.Fragment />;
	};

	const getButtonContent = () => (
		<React.Fragment>
			{renderIcon(options.find(item => item.selected))}
			{titleValue || props.buttonText || props.placeholder}
		</React.Fragment>
	);

	const renderIcon = (item: IItem) => {
		if (item) {
			return GetIconComponent(item.icon, {
				title: item.externalData?.iconTitle || undefined,
				className: item.externalData?.iconClassName || undefined
			});
		}
	};

	const bcButtonClassName = cx({
		picker: true,
		picker__placeholder: !titleValue && !props.buttonText && props.placeholder
	});

	const isDisabled = props.readonly || props.disabled;

	return (
		<React.Fragment>
			<div>
				<BCButtonGroup
					className={Styles.buttonGroup}
					key="btnGroup"
				>
					<BCButton
						onClick={onClickPickerButton}
						className={bcButtonClassName}
						disabled={isDisabled}
						style={props.style}
						size={props.size}
					>
						{getButtonContent()}
					</BCButton>
					<BCButton
						disabled={isDisabled || !props.allowClear}
						className={Styles.suffixButton}
						onClick={() => clearValue()}
						icon={<IconClear />}
						size={props.size}
					/>
				</BCButtonGroup>
			</div>

			<Picker
				confirmText={findMessage.get('100004')}
				cancelText={findMessage.get('100034')}
				getContainer={props.getPopupContainer}
				title={getSearchInput()}
				columns={[getItems()]}
				value={[props.value]}
				onConfirm={onConfirm}
				visible={visible}
				onClose={onClose}
				loading={props.loading}
				destroyOnClose
			/>
		</React.Fragment>
	);
}
